// Hybrid routing provider — runs HERE and Google Routes in parallel
// then picks the "best" route using a heuristic that combines distance,
// duration and a compliance proxy. The compliance proxy is the
// geometry divergence between the two providers: when one provider
// produces a much shorter route AND the geometries diverge in a
// specific section, that's a strong signal it's taking a shortcut
// through a road the other provider knows is restricted (vias
// interditas, sentidos únicos, ruas privadas).
//
// Returns the same shape as calculateRouteHERE / calculateRouteGoogle
// (route, alternatives, warning) plus two extras for the UI:
//   hybridProvider: 'here' | 'google'   — which one was picked
//   hybridMeta: { ... }                  — comparison stats for tooltip

import { calculateRouteHERE } from './hereApi';
import { calculateRouteGoogle } from './googleRoutesApi';

function haversineM(a, b) {
  if (!a || !b) return Infinity;
  const R = 6371000;
  const toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const x = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(a[1])) * Math.cos(toRad(b[1])) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
}

// Distance from a point to the nearest vertex of a polyline. Cheaper
// than projecting onto each segment and accurate enough for our
// "is the route going through a different street" question.
function nearestDistanceToLine(point, line) {
  let min = Infinity;
  for (const p of line) {
    const d = haversineM(point, p);
    if (d < min) min = d;
  }
  return min;
}

// Subsample a polyline down to at most `maxPoints` points by keeping
// every Nth vertex. Critical for long routes (Lisbon → London can
// produce 10k+ points per provider, and a naïve O(N×M) divergence
// check would freeze the browser). 500 points is more than enough to
// detect whether two routes share most of their path.
function subsample(line, maxPoints = 500) {
  if (!Array.isArray(line) || line.length <= maxPoints) return line || [];
  const step = Math.max(1, Math.ceil(line.length / maxPoints));
  const out = [];
  for (let i = 0; i < line.length; i += step) out.push(line[i]);
  if (out[out.length - 1] !== line[line.length - 1]) out.push(line[line.length - 1]);
  return out;
}

// Fraction (0-1) of route A's vertices that lie further than `threshold`
// metres from any vertex of route B. Operates on subsampled lines so
// the cost is bounded — 500 × 500 = 250k haversines, < 100ms even on
// trans-continental routes. Returns 0 when one of the lines is empty.
function divergencePct(routeA, routeB, threshold = 100) {
  if (!Array.isArray(routeA) || !Array.isArray(routeB) || routeA.length < 2 || routeB.length < 2) return 0;
  const sa = subsample(routeA, 500);
  const sb = subsample(routeB, 500);
  let diverged = 0;
  for (const p of sa) {
    if (nearestDistanceToLine(p, sb) > threshold) diverged++;
  }
  return sa.length ? diverged / sa.length : 0;
}

// Decide which route is better. The heuristic:
//   1. >85% overlap → both providers agree → pick the cheaper one
//   2. One ≥15% shorter than the other AND geometries diverge → the
//      shorter is suspicious (likely a shortcut through a restricted
//      road) → pick the longer one
//   3. Similar distance, different paths → pick faster (less affected
//      by intermediate restrictions)
function pickBest(here, google, opts = {}) {
  // preferGoogleOnTie: para rotas de assistência o utilizador prefere
  // Google em empate (melhor dados de restrições urbanas em PT/ES).
  // Google Routes API já considera vias interditas e tráfego real-time
  // por defeito, por isso é seguro favorecê-lo nestes casos.
  const { preferGoogleOnTie = false } = opts;
  const tieWinner = preferGoogleOnTie ? 'google' : 'here';
  const aGeom = here.geometry?.coordinates || [];
  const bGeom = google.geometry?.coordinates || [];
  const aLen = here.distance || 0;
  const bLen = google.distance || 0;

  // Divergence is computed on subsampled lines to keep cost bounded
  // for transcontinental routes. We measure both directions because
  // route A may follow B faithfully while B has detours not in A.
  const divPctHB = divergencePct(aGeom, bGeom);
  const divPctBH = divergencePct(bGeom, aGeom);
  const overlapPct = Math.max(0, 1 - Math.max(divPctHB, divPctBH));

  const meta = {
    overlapPct: Math.round(overlapPct * 100),
    hereDistKm: (aLen / 1000).toFixed(1),
    googleDistKm: (bLen / 1000).toFixed(1),
    hereDurMin: Math.round((here.duration || 0) / 60),
    googleDurMin: Math.round((google.duration || 0) / 60),
  };

  // Both providers agree (>85% same path) → pick whichever is cheaper.
  // Em empate de distância, respeita-se tieWinner.
  if (overlapPct > 0.85) {
    let cheaper;
    if (aLen < bLen) cheaper = 'here';
    else if (bLen < aLen) cheaper = 'google';
    else cheaper = tieWinner;
    return {
      route: cheaper === 'here' ? here : google,
      alternatives: cheaper === 'here' ? [google] : [here],
      provider: cheaper,
      reason: `Híbrido · HERE e Google concordam (${meta.overlapPct}% sobreposição) · escolhido ${cheaper.toUpperCase()}${aLen === bLen ? ' por desempate' : ' por ser ligeiramente mais curto'}`,
      meta,
    };
  }

  // HERE significantly shorter than Google AND geometries diverge →
  // HERE may have taken a shortcut through a restricted road that
  // Google flagged. Trust Google.
  if (aLen > 0 && bLen > 0 && aLen < bLen * 0.85) {
    return {
      route: google,
      alternatives: [here],
      provider: 'google',
      reason: `Híbrido · HERE ${meta.hereDistKm} km vs Google ${meta.googleDistKm} km e os caminhos divergem (${meta.overlapPct}% sobreposição) · escolhido Google porque o atalho do HERE pode passar por via interdita`,
      meta,
    };
  }
  // Symmetric case — Google much shorter and paths diverge.
  if (aLen > 0 && bLen > 0 && bLen < aLen * 0.85) {
    return {
      route: here,
      alternatives: [google],
      provider: 'here',
      reason: `Híbrido · Google ${meta.googleDistKm} km vs HERE ${meta.hereDistKm} km e os caminhos divergem · escolhido HERE porque o atalho do Google pode não ser viável`,
      meta,
    };
  }

  // Distances are similar (within 15%) but paths differ — pick the
  // faster one as a tiebreaker. Empate exacto em duração → tieWinner.
  const hereDur = here.duration || 0;
  const googleDur = google.duration || 0;
  let winner;
  if (hereDur < googleDur) winner = 'here';
  else if (googleDur < hereDur) winner = 'google';
  else winner = tieWinner;
  if (winner === 'here') {
    return {
      route: here,
      alternatives: [google],
      provider: 'here',
      reason: `Híbrido · HERE ${meta.hereDurMin} min vs Google ${meta.googleDurMin} min · escolhido HERE${hereDur === googleDur ? ' por desempate' : ' por ser mais rápido em caminhos comparáveis'}`,
      meta,
    };
  }
  return {
    route: google,
    alternatives: [here],
    provider: 'google',
    reason: `Híbrido · Google ${meta.googleDurMin} min vs HERE ${meta.hereDurMin} min · escolhido Google${hereDur === googleDur ? ' por desempate (rota de assistência — restrições urbanas)' : ' por ser mais rápido em caminhos comparáveis'}`,
    meta,
  };
}

/**
 * Compute a route via HERE and Google in parallel and return the best
 * one. Same return shape as calculateRouteHERE / calculateRouteGoogle.
 *
 * If one provider fails, the other is used and a warning is set.
 * If both fail, throws.
 */
export async function calculateRouteHybrid(origin, destination, waypoints, options) {
  const [hereResult, googleResult] = await Promise.allSettled([
    calculateRouteHERE(origin, destination, waypoints, options),
    calculateRouteGoogle(origin, destination, waypoints, options),
  ]);

  // Tolerate single-provider failure — still produce a usable route.
  if (hereResult.status === 'rejected' && googleResult.status === 'fulfilled') {
    return {
      ...googleResult.value,
      hybridProvider: 'google',
      hybridMeta: null,
      warning: `Híbrido degradado · HERE falhou (${hereResult.reason?.message?.slice(0, 80) || 'erro desconhecido'}) · Google usado em sua substituição`,
    };
  }
  if (googleResult.status === 'rejected' && hereResult.status === 'fulfilled') {
    return {
      ...hereResult.value,
      hybridProvider: 'here',
      hybridMeta: null,
      warning: `Híbrido degradado · Google falhou (${googleResult.reason?.message?.slice(0, 80) || 'erro desconhecido'}) · HERE usado em sua substituição`,
    };
  }
  if (hereResult.status === 'rejected' && googleResult.status === 'rejected') {
    throw new Error(
      'Ambos os providers de rotas falharam · HERE: '
      + (hereResult.reason?.message || '?')
      + ' · Google: '
      + (googleResult.reason?.message || '?')
    );
  }

  const here = hereResult.value;
  const google = googleResult.value;

  const decision = pickBest(here.route, google.route, {
    preferGoogleOnTie: !!options?.preferGoogleOnTie,
  });

  // Quando o Google vence, herda o `countryDistances` do HERE — o
  // Google Routes API não devolve spans por país (fieldMask limitado)
  // e cai num único bucket = regionCode. O HERE tem o breakdown
  // correcto e já o calculou em paralelo, por isso copiamos-lhe os
  // dados sem custo de chamada extra. Sem isto, o card "Combustível
  // por País" em rotas multi-país (Lisboa→Belfast etc.) mostrava
  // apenas Portugal com a distância total.
  const winnerRoute = decision.route;
  if (decision.provider === 'google'
      && here.route?.countryDistances
      && Object.keys(here.route.countryDistances).length > 1) {
    winnerRoute.countryDistances = here.route.countryDistances;
  }
  return {
    route: winnerRoute,
    alternatives: decision.alternatives,
    warning: decision.reason,
    hybridProvider: decision.provider,
    hybridMeta: decision.meta,
  };
}
